import {
  useCallback,
  useEffect,
  useLayoutEffect,
  useRef,
  useState,
} from "react";

import { chatTurns } from "src/analytics/copilot/LiveAnalyticsMessages";
import type { SessionMessagesResponseMessagesInner } from "src/clients/agent-management";

const FOLLOW_BOTTOM_THRESHOLD_PX = 50;
const SCROLL_KEYS = new Set([
  "PageUp",
  "PageDown",
  "ArrowUp",
  "ArrowDown",
  "Home",
  "End",
]);

type UseAnalyticsChatScrollParams = {
  listRef: React.RefObject<HTMLUListElement | null>;
  lastTurnRef: React.RefObject<HTMLLIElement | null>;
  messages: SessionMessagesResponseMessagesInner[];
  isActive: boolean;
  failureMessage: string | null;
};

type UseAnalyticsChatScrollResult = {
  reservesTurnHeight: boolean;
  snapPromptToTop: () => void;
};

export const useAnalyticsChatScroll = ({
  listRef,
  lastTurnRef,
  messages,
  isActive,
  failureMessage,
}: UseAnalyticsChatScrollParams): UseAnalyticsChatScrollResult => {
  const [reservesTurnHeight, setReservesTurnHeight] = useState(false);

  const snapFromTurnsRef = useRef<number | null>(null);
  const hasSnappedRef = useRef(false);
  const followsBottomRef = useRef(true);
  const isPinnedRef = useRef(false);
  const rafRef = useRef(0);

  const turns = chatTurns(messages);
  const turnCount = turns.length;
  const messageCount = turns.reduce(
    (count, turn) => count + turn.messages.length,
    0,
  );
  const hasAnswer = (turns.at(-1)?.messages.length ?? 0) > 1;

  const turnCountRef = useRef(turnCount);
  turnCountRef.current = turnCount;

  const snapFromTurns = snapFromTurnsRef.current;
  const isPinned = snapFromTurns !== null && turnCount > snapFromTurns;
  isPinnedRef.current = isPinned;

  // Set during render so the reservation is in the same commit as the snap;
  // scrollIntoView clamps to the space that exists when it runs.
  if (isPinned && !reservesTurnHeight) setReservesTurnHeight(true);
  if (turnCount === 0 && reservesTurnHeight) setReservesTurnHeight(false);

  const snapPromptToTop = useCallback(() => {
    snapFromTurnsRef.current = turnCountRef.current;
    hasSnappedRef.current = false;
    // The snap owns the scroll position from here; following the bottom would
    // yank the prompt off-screen once the pin releases.
    followsBottomRef.current = false;
  }, []);

  useLayoutEffect(() => {
    const list = listRef.current;
    if (!list) return;

    cancelAnimationFrame(rafRef.current);

    if (turnCount === 0) {
      hasSnappedRef.current = false;
      // A snap armed at zero turns is the first prompt in flight, not a reset.
      if (snapFromTurnsRef.current !== 0) {
        snapFromTurnsRef.current = null;
        followsBottomRef.current = true;
      }
      return;
    }
    if (isPinned) {
      if (hasSnappedRef.current) return;
      hasSnappedRef.current = true;
      lastTurnRef.current?.scrollIntoView({
        block: "start",
        // A hidden tab has no frames to animate with.
        behavior: document.hidden ? "instant" : "auto",
      });
      return;
    }
    if (!followsBottomRef.current) return;
    rafRef.current = requestAnimationFrame(() => {
      list.scrollTo({ top: list.scrollHeight });
    });
  }, [
    isPinned,
    lastTurnRef,
    listRef,
    turnCount,
    messageCount,
    isActive,
    failureMessage,
    reservesTurnHeight,
  ]);

  useEffect(() => {
    const list = listRef.current;
    if (!list) return;

    // Every way the user can move the list opens with one of these events;
    // the pin's own scroll emits only scroll events, ignored while pinned.
    const releasePin = () => {
      snapFromTurnsRef.current = null;
      isPinnedRef.current = false;
    };

    // A click on the messages lands on a child; only the scrollbar and the
    // list's own padding hit the list itself.
    const handlePointerDown = (event: Event) => {
      if (event.target === list) releasePin();
    };

    const handleKeyDown = (event: KeyboardEvent) => {
      if (SCROLL_KEYS.has(event.key)) releasePin();
    };

    const handleScroll = () => {
      if (isPinnedRef.current) return;
      followsBottomRef.current =
        list.scrollHeight - list.scrollTop - list.clientHeight <
        FOLLOW_BOTTOM_THRESHOLD_PX;
    };

    list.addEventListener("scroll", handleScroll);
    list.addEventListener("wheel", releasePin);
    list.addEventListener("touchmove", releasePin);
    list.addEventListener("pointerdown", handlePointerDown);
    list.addEventListener("keydown", handleKeyDown);
    return () => {
      list.removeEventListener("scroll", handleScroll);
      list.removeEventListener("wheel", releasePin);
      list.removeEventListener("touchmove", releasePin);
      list.removeEventListener("pointerdown", handlePointerDown);
      list.removeEventListener("keydown", handleKeyDown);
    };
  }, [listRef]);

  useEffect(() => {
    if (snapFromTurnsRef.current === null || isActive) return;
    if (hasAnswer || failureMessage) {
      snapFromTurnsRef.current = null;
      isPinnedRef.current = false;
    }
  }, [isActive, hasAnswer, failureMessage]);

  return { reservesTurnHeight, snapPromptToTop };
};
